import styled from "@emotion/styled";
import { Bar } from "react-chartjs-2";
import { ThemeProps } from "types/theme";
import { withTheme } from "@emotion/react";
import {
  Align,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Title,
} from "chart.js";
import { customOrange } from "core/theme/variants";
import { Grid, Typography } from "@mui/material";
import { SoloBar } from "components/atoms/solo-bar";
import { useTranslation } from "react-i18next";

ChartJS.register(LinearScale, CategoryScale, BarElement, Title);

const ChartWrapper = styled.div`
  height: 100px;
  position: relative;
`;

type Props = {
  title: string;
  countryValues: { [key: string]: number };
};

const CountryChart = ({ theme, title, countryValues }: ThemeProps & Props) => {
  const { t } = useTranslation();
  const totalValue = countryValues
    ? Object.values(countryValues).reduce((acc, value) => acc + value, 0)
    : 1;

  const countryReduced = countryValues
    ? Object.entries(countryValues)
        .sort(([, a], [, b]) => b - a)
        .reduce<{ [key: string]: number }>((acc, [country, value], index) => {
          const percentage = (value / totalValue) * 100;
          if (index < 5) {
            acc[country] = percentage;
          } else {
            acc["Other"] = (acc["Other"] || 0) + percentage;
          }
          return acc;
        }, {})
    : {};

  const orangeColors = [
    customOrange[600],
    customOrange[500],
    customOrange[400],
    customOrange[300],
    customOrange[200],
    customOrange[100],
  ];

  const datasets = Object.entries(countryReduced).map(
    ([key, value], index) => ({
      label: key,
      data: [value],
      backgroundColor: orangeColors[index % orangeColors.length],
      borderColor: orangeColors[index % orangeColors.length],
      borderWidth: 0,
      hoverBackgroundColor: theme.palette.primary.main,
      hoverBorderColor: theme.palette.primary.main,
      borderRadius: 6,
      barThickness: 24,
      barPercentage: 1,
      categoryPercentage: 1,
    })
  );

  const data: ChartData<"bar", number[], string> = {
    labels: [""],
    datasets: datasets,
  };

  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    indexAxis: "y" as undefined | "y" | "x",
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        align: "start" as Align,
        font: {
          weight: 700,
        },
        fullSize: true,
      },
    },
    scales: {
      x: {
        display: false,
        max: 100,
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        display: false,
        stacked: true,
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <>
      <ChartWrapper>
        <Bar data={data} options={options} />
      </ChartWrapper>
      {countryValues && Object.keys(countryValues).length === 0 && (
        <Typography>{t("empty_states.empty_stats.title")}</Typography>
      )}
      <Grid container spacing={2}>
        {countryReduced &&
          Object.entries(countryReduced).map(([key, value], index) => (
            <Grid item xs={12}>
              <SoloBar
                color={orangeColors[index % orangeColors.length]}
                country={key}
                percentage={value}
              />
            </Grid>
          ))}
      </Grid>
    </>
  );
};

export default withTheme(CountryChart);
